"use client";

import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { Suspense, useState } from "react";

import { safeNext, useRegister } from "../../../_lib/hooks";
import type { LegalKind } from "../../../_lib/siteConfig";
import { useSite } from "../../../_lib/useSite";
import { Tooltip } from "../../../_ui/Tooltip";
import { hyphenate } from "../shell/buy";
import { CheckRow, TextField } from "../shell/form";
import { useSimpleLegal } from "../shell/LegalSheet";
import { Action, Card, IconChevron, PageTitle, Problem, Waiting } from "../shell/parts";
import { Shell } from "../shell/Shell";

/*
 * 회원 가입 · 약관 동의 — 시안 09 (`/register`) · 약관 시트 10.
 *
 * 🔴 **동의는 항목별입니다**(README §11-5 · 개인정보보호법). «전체 동의» 는 나머지를 한 번에 켜고
 *    끄는 **편의 장치**이고, 항목은 각각 따로 켜고 끕니다. 필수 셋이 다 켜져야 가입합니다.
 * 🔴 **약관 «보기» 는 시트**입니다 — 적던 폼이 그대로 남습니다. 본문이 채워진 약관만 «보기» 를
 *    그립니다(`useSite().legal`). 시트의 «동의합니다» 가 그 항목을 켭니다.
 * 🔴 **식별자는 이메일**입니다(`useRegister` — 서버가 `email` 을 받습니다). 시안의 «아이디 · 중복 확인»
 *    은 이메일 칸 하나로 — 중복은 가입할 때 서버가 칸 옆 오류로 알립니다(`docs/base/11` §7-3).
 * 🔑 휴대폰 인증은 **자리만**(README §11-6 · 본인인증 계약 전) — «인증번호» 는 누를 수 없고,
 *    번호 자체는 선택 항목으로 저장됩니다.
 * ⚠️ 시안의 «(선택) 혜택·이벤트 정보 수신» 은 두지 않았습니다 — 서버에 그 동의를 저장할 칸이 없어,
 *    켜도 어디에도 남지 않는 체크를 그리게 됩니다(선택 동의는 따로 저장돼야 합니다 · README §11-5).
 * 🔑 추천 코드 = 가입 때 정하는 판매처 코드(`seller_code`) — 🔴 가입 뒤에는 바꿀 수 없습니다.
 */
export default function Register() {
  return (
    <Shell top="loose">
      <Suspense fallback={<Card><Waiting /></Card>}>
        <RegisterForm />
      </Suspense>
    </Shell>
  );
}

function RegisterForm() {
  const params = useSearchParams();
  const next = safeNext(params.get("next"));
  const { legal } = useSite();
  const legalSheet = useSimpleLegal();
  const { register, busy, error, fields } = useRegister();

  const [mobile, setMobile] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [name, setName] = useState("");
  const [sellerCode, setSellerCode] = useState("");
  const [agree, setAgree] = useState({ terms: false, privacy: false, age: false });
  const [localError, setLocalError] = useState<{ password2?: string; agree?: string }>({});

  const all = agree.terms && agree.privacy && agree.age;
  const has = (kind: LegalKind) => legal.some((doc) => doc.kind === kind);

  function set(key: keyof typeof agree, on: boolean) {
    setAgree((prev) => ({ ...prev, [key]: on }));
    setLocalError((prev) => ({ ...prev, agree: undefined }));
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (busy) return;
    const problems: typeof localError = {};
    if (password !== password2) problems.password2 = "비밀번호가 서로 다릅니다.";
    if (!all) problems.agree = "필수 항목에 모두 동의해 주세요.";
    setLocalError(problems);
    if (problems.password2 || problems.agree) return;
    await register({ email, password, name, mobile, sellerCode }, next);
  }

  const view = (kind: LegalKind, key: keyof typeof agree) =>
    has(kind) ? (
      <Tooltip text="약관 전문을 이 화면 위에 엽니다." align="right">
        {(trigger) => (
          <button type="button" onClick={() => legalSheet.open(kind, () => set(key, true))} className="flex flex-none items-center gap-px text-[length:calc(var(--tnf-text-base)*13.5/14)] text-ink-faint hover:text-ink" {...trigger}>
            보기
            <IconChevron dir="right" size={18} />
          </button>
        )}
      </Tooltip>
    ) : undefined;

  return (
    <>
      <PageTitle>회원 가입</PageTitle>
      <form onSubmit={submit} noValidate>
        <Card>
          <div className="grid gap-3.5">
            {error && !Object.values(fields).some(Boolean) && <Problem message={error} />}
            <TextField
              label="휴대폰 번호"
              type="tel"
              inputMode="numeric"
              autoComplete="tel"
              optional
              value={mobile}
              onChange={(v) => setMobile(hyphenate(v))}
              placeholder="010-0000-0000"
              error={fields.mobile || undefined}
              trailing={
                <Tooltip text="휴대폰 인증은 준비 중입니다." align="right">
                  {(trigger) => (
                    <button type="button" aria-disabled="true" className="h-[54px] flex-none cursor-not-allowed rounded-[var(--tnf-radius-sm)] bg-surface px-[15px] text-body font-bold text-ink-faint shadow-[inset_0_0_0_1px_var(--tnf-line)]" {...trigger}>
                      인증번호
                    </button>
                  )}
                </Tooltip>
              }
            />
            <TextField label="이메일" type="email" inputMode="email" autoComplete="email" required value={email} onChange={setEmail} placeholder="hong@example.com" error={fields.email || undefined} />
            <TextField label="비밀번호" type="password" autoComplete="new-password" required value={password} onChange={setPassword} placeholder="8자 이상" error={fields.password || undefined} />
            <TextField label="비밀번호 확인" type="password" autoComplete="new-password" required value={password2} onChange={(v) => { setPassword2(v); setLocalError((p) => ({ ...p, password2: undefined })); }} placeholder="••••••••" error={localError.password2} />
            <TextField label="이름" autoComplete="name" required value={name} onChange={setName} placeholder="실명" error={fields.name || undefined} />
            <TextField label="추천 코드" optional value={sellerCode} onChange={setSellerCode} placeholder="가맹점에서 받은 코드" hint="가입 뒤에는 바꿀 수 없습니다." error={fields.seller_code || undefined} />
          </div>
        </Card>

        <div className="h-3" />

        <Card className="!p-4">
          <CheckRow strong checked={all} onChange={(on) => { setAgree({ terms: on, privacy: on, age: on }); setLocalError((p) => ({ ...p, agree: undefined })); }}>
            전체 동의
          </CheckRow>
          <div className="h-px bg-line" />
          <CheckRow checked={agree.terms} onChange={(on) => set("terms", on)} after={view("terms", "terms")}>
            <b className="font-bold text-ink">(필수)</b> 이용약관
          </CheckRow>
          <CheckRow checked={agree.privacy} onChange={(on) => set("privacy", on)} after={view("privacy", "privacy")}>
            <b className="font-bold text-ink">(필수)</b> 개인정보 수집·이용
          </CheckRow>
          <CheckRow checked={agree.age} onChange={(on) => set("age", on)}>
            <b className="font-bold text-ink">(필수)</b> 만 14세 이상입니다
          </CheckRow>
          {localError.agree && <p role="alert" className="mx-0.5 mt-1 text-caption font-bold text-danger">{localError.agree}</p>}
        </Card>

        <div className="mt-4">
          <Action type="submit" size="xl" loading={busy} loadingText="가입하는 중입니다" tooltip="입력한 정보로 가입하고 하던 화면으로 돌아갑니다.">
            가입하기
          </Action>
        </div>
      </form>
      <p className="mt-3.5 text-center text-body text-ink-faint">
        이미 계정이 있으신가요?{" "}
        <Link href={next === "/" ? "/login" : `/login?next=${encodeURIComponent(next)}`} className="font-bold text-ink underline-offset-4 hover:underline">로그인</Link>
      </p>
      {legalSheet.sheet}
    </>
  );
}
